import type { EffectCallback } from 'react';
import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';

/*
 * This hook does a deep compare on the dependencies array and only calls the effect if the dependencies have changed.
 * This ensures that a component is not re-rendered when an object reference has changed, but the object itself has not.
 * It is inspired by https://stackoverflow.com/q/55808749 and https://github.com/streamich/react-use
 * The current implementation does not support the cleanup function.
 */
export function useDeepCompareEffect<T>(effect: EffectCallback, dependencies: T[]): void {
    if (!dependencies.length) {
        throw new Error('`useDeepCompareEffect` should not be used with no dependencies. Use React.useEffect instead.');
    }

    const prevDependencies = useRef<T[] | undefined>();
    const dependenciesAreEqual = prevDependencies.current
        ? prevDependencies.current.every((element, index) => isEqual(element, dependencies[index]))
        : false;
    if (!dependenciesAreEqual) {
        prevDependencies.current = dependencies;
    }

    useEffect(effect, prevDependencies.current);
}
